import { CalculatorOutlined, DeleteOutlined, DownOutlined, PlusOutlined, UpOutlined } from '@ant-design/icons';
import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Table,
  Typography,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import {
  updateField,
  updatePmAdditionalActContractProjectProjectWorkPlaceItem as updatePmAdditionalActContractProjectWorkPlaceItem,
} from '../../../../../../contextRedux/PMAdditionalActContractProject/PmAdditionalActContractProjectModel';
import { RootState } from '../../../../../../store';
import { Column } from '../../../../../../utils/column';
import './LaborTable.css';

const { Title, Text } = Typography;

const LaborTable: React.FC<any> = ({}) => {
  const isMobile = useMediaQuery({ query: '(max-width: 992px)' });
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const [laborTableData, setLaborTableData] = useState<any>([]);
  const pmAdditionalActContractProject = useSelector(
    (state: RootState) => state?.models?.pmAdditionalActContractProject
  );
  const addedDevices = useSelector(
    (state: RootState) => state?.models?.pmAdditionalActContractProject.pmContractWorkPlaceItems
  );
  const laborExtras = useSelector((state: RootState) => state?.store?.laborExtra?.data);
  const unitMeasures = useSelector((state: RootState) => state?.store?.unitMeasure?.data);
  const company = useSelector((state: RootState) =>
    state.store.company.data.find((c) => c.id == state.models.pmAdditionalActContractProject.companyId)
  );
  const workPlaces = company?.workPlaces;

  const [selectedOption, setSelectedOption] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  useEffect(() => {
    form.setFieldValue('pmContractLaborExtras', pmAdditionalActContractProject.pmContractLaborExtras);
  }, [form, pmAdditionalActContractProject.pmContractLaborExtras]);

  useEffect(() => {
    const pmContractLaborExtras = pmAdditionalActContractProject.pmContractLaborExtras;

    const totalLabor = Number.parseFloat(
      (
        addedDevices.reduce((total, item) => total + item.laborPrice, 0) +
        (pmContractLaborExtras
          ? pmContractLaborExtras.reduce((total: any, item: any) => total + item?.piece * item?.price, 0)
          : 0)
      ).toFixed(2)
    );

    const totalProductsValue = Number.parseFloat(
      addedDevices.reduce((total, item) => {
        const totalPrice = item.price;
        return total + totalPrice;
      }, 0)
    );

    const percent = Number.parseFloat(((totalLabor / (totalLabor + totalProductsValue)) * 100).toFixed(2));

    const updatedLaborTableData = [
      {
        key: 'total',
        totalLabor: totalLabor,
        totalProductsValue: totalProductsValue,
        percent: percent >= 1 ? percent : 1,
        totalLaborAndProducts: Number.parseFloat((totalLabor + totalProductsValue).toFixed(2)),
      },
    ];
    setLaborTableData(updatedLaborTableData);
  }, [addedDevices, pmAdditionalActContractProject.pmContractLaborExtras, form]);

  const handleSelectLabor = (value: string) => {
    const labor = laborExtras.find((date) => date?.name === value);
    const fieldsValue = {
      ...form.getFieldsValue(true),
      laborExtra: labor,
      laborExtraId: labor?.id,
    };
    form.setFieldsValue(fieldsValue);
  };

  const handleSelectWorkPlaceLabor = (value: string) => {
    const workPlace = workPlaces.find((ad: any) => ad?.name == value);

    const fieldsValue = {
      ...form.getFieldsValue(true),
      workPlace: workPlace,
      workPlaceId: workPlace?.id,
    };
    form.setFieldsValue(fieldsValue);
  };

  const handleFilter = (inputValue: string, option: any) => {
    return option.value.toLowerCase().indexOf(inputValue.toLowerCase()) !== -1;
  };

  const handleCheckboxChange = (option: any) => {
    setSelectedOption(option); // Set the selected option
  };

  const handlePercentageChange = () => {
    const pmContractLaborExtras = pmAdditionalActContractProject.pmContractLaborExtras;

    const totalLabor = Number.parseFloat(
      addedDevices.reduce((total, item) => total + item.laborPrice, 0).toFixed(2) +
        pmContractLaborExtras.reduce((total: any, item: any) => total + item?.piece * item?.price, 0).toFixed(2)
    );

    const totalProductsValue = Number.parseFloat(
      addedDevices
        .reduce((total, item) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );

    const percent = Number.parseFloat((laborTableData[0].percent / 100).toFixed(2));
    const a = Number.parseFloat((percent * totalProductsValue).toFixed(2));
    const b = Number.parseFloat((a / (1 - percent)).toFixed(2));
    const c = Number.parseFloat((b / totalLabor).toFixed(2));

    const factor = c;

    const updatedTable = addedDevices?.map((item) => {
      // Create a new copy of the item
      let newItem = { ...item };

      if (item.unitMeasure === 'hour') {
        // Update laborPriceOnHour and laborPrice
        newItem.laborPriceOnHour =
          Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2)) >= 1
            ? Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2))
            : 1;
        newItem.laborPrice = Number.parseFloat((newItem.laborPriceOnHour * item.piece).toFixed(2));
      } else {
        // Deep clone the device object to avoid mutation of the original object
        let newDevice = { ...item.device };

        newDevice.laborPricePM =
          item.laborPrice / item.piece > item.device.laborPriceSM
            ? Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2)) >= 1
              ? Number.parseFloat(((item.laborPrice / item.piece) * factor).toFixed(2))
              : 1
            : Number.parseFloat((item.device.laborPricePM * factor).toFixed(2)) >= 1
            ? Number.parseFloat((item.device.laborPricePM * factor).toFixed(2))
            : 1;

        newItem.device = newDevice; // Assign the new deep-copied device to newItem
        newItem.laborPrice = Number.parseFloat((newDevice.laborPricePM * item.piece).toFixed(2));
      }
      return newItem;
    });

    const updatedLaborExtraItems = pmContractLaborExtras.map((ole: any) => {
      let newItem = { ...ole };
      newItem.price = Number.parseFloat(((newItem.price * factor * newItem.piece) / newItem.piece).toFixed(2));
      newItem.totalManopera = Number.parseFloat((newItem.price * newItem.piece).toFixed(2));

      return newItem;
    });

    dispatch(updateField({ field: 'pmContractLaborExtras', value: updatedLaborExtraItems }));
    form.setFieldValue('pmContractLaborExtras', updatedLaborExtraItems);

    updatedTable.map((ut: any) => {
      dispatch(
        updatePmAdditionalActContractProjectWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === ut.deviceTypeId && item.workPlaceId === ut.workPlaceId
          ),
          updates: ut,
        })
      );
    });
  };

  const handleTotalLaborAndProductsChange = () => {
    const pmContractLaborExtras = pmAdditionalActContractProject.pmContractLaborExtras;

    const totalLabor = Number.parseFloat(
      (
        addedDevices.reduce((total, item) => total + item.laborPrice, 0) +
        (pmContractLaborExtras
          ? pmContractLaborExtras.reduce((total: any, item: any) => total + item?.piece * item?.price, 0)
          : 0)
      ).toFixed(2)
    );

    const totalProductsValue = Number.parseFloat(
      addedDevices
        .reduce((total, item) => {
          const totalPrice = item.price;
          return total + totalPrice;
        }, 0)
        .toFixed(2)
    );

    // Target total values (e.g., set by the user)
    const targetTotal = laborTableData[0].totalLaborAndProducts;

    // Determine factors based on the selected option
    let laborFactor = 1;
    let productFactor = 1;

    if (selectedOption === 'updatePriceWithAddition') {
      productFactor = Number.parseFloat(
        (1 + (targetTotal - (totalLabor + totalProductsValue)) / totalProductsValue).toFixed(4)
      );
    } else if (selectedOption === 'updateLaborPrice') {
      laborFactor = Number.parseFloat((1 + (targetTotal - (totalLabor + totalProductsValue)) / totalLabor).toFixed(4));
    } else if (selectedOption === 'updatePriceWithAdditionAndLaborPrice') {
      // Split the adjustment between labor and products proportionally
      const currentTotal = totalLabor + totalProductsValue;
      const totalDelta = targetTotal - currentTotal;

      const laborRatio = totalLabor / currentTotal;
      const productsRatio = totalProductsValue / currentTotal;

      const laborAdjustment = totalDelta * laborRatio;
      const productsAdjustment = totalDelta * productsRatio;

      laborFactor = Number.parseFloat((1 + laborAdjustment / totalLabor).toFixed(4));
      productFactor = Number.parseFloat((1 + productsAdjustment / totalProductsValue).toFixed(4));
    }

    // Update the devices based on the selected option and calculated factors
    const updatedDevices = addedDevices.map((item) => {
      let updatedItem = { ...item };

      // If updating products' price with addition
      if (selectedOption === 'updatePriceWithAddition' || selectedOption === 'updatePriceWithAdditionAndLaborPrice') {
        const newPrice = Math.max(
          Number.parseFloat((item.price * productFactor).toFixed(2)),
          item.device.price * item.piece
        );

        const newAddition = Number.parseFloat(
          (((newPrice - item.device.price * item.piece) / (item.device.price * item.piece)) * 100).toFixed(2)
        );

        updatedItem.price = newPrice;
        updatedItem.addition = Math.max(newAddition, 1);
      }

      // If updating labor prices
      if (selectedOption === 'updateLaborPrice' || selectedOption === 'updatePriceWithAdditionAndLaborPrice') {
        if (item.unitMeasure === 'hour') {
          const newLaborPriceOnHour = Math.max(
            Number.parseFloat(((item.laborPrice / item.piece) * laborFactor).toFixed(2)),
            1
          );
          updatedItem.laborPriceOnHour = newLaborPriceOnHour;
          updatedItem.laborPrice = Number.parseFloat((newLaborPriceOnHour * item.piece).toFixed(2));
        } else {
          const updatedDevice = { ...item.device };

          updatedDevice.laborPricePM = Math.max(
            Number.parseFloat(((item.laborPrice / item.piece) * laborFactor).toFixed(2)),
            1
          );

          updatedItem.device = updatedDevice;
          updatedItem.laborPrice = Number.parseFloat((updatedDevice.laborPricePM * item.piece).toFixed(2));
        }
      }

      return updatedItem;
    });

    const updatedLaborExtraItems = pmAdditionalActContractProject.pmContractLaborExtras.map((extraItem: any) => {
      const updatedExtra = { ...extraItem };

      updatedExtra.price = Number.parseFloat(
        ((extraItem.price * laborFactor * extraItem.piece) / extraItem.piece).toFixed(2)
      );

      updatedExtra.totalManopera = Number.parseFloat((updatedExtra.price * updatedExtra.piece).toFixed(2));

      return updatedExtra;
    });

    // Update the summary row with the adjusted labor extra items
    dispatch(updateField({ field: 'pmContractLaborExtras', value: updatedLaborExtraItems }));

    // Set updated values in the form and dispatch changes
    form.setFieldValue('pmContractLaborExtras', updatedLaborExtraItems);

    updatedDevices.map((ut: any) => {
      dispatch(
        updatePmAdditionalActContractProjectWorkPlaceItem({
          index: addedDevices.findIndex(
            (item: any) => item.deviceTypeId === ut.deviceTypeId && item.workPlaceId === ut.workPlaceId
          ),
          updates: ut,
        })
      );
    });
    setIsModalVisible(false);
  };

  const onChangePercent = (value: number) => {
    let updatedData = [...laborTableData];
    updatedData[0].percent = Number.parseFloat(value != null ? value.toFixed(2) : updatedData[0].percent);

    setLaborTableData(updatedData);
  };

  const onChangeLaborAndProducts = (value: number) => {
    let updatedData = [...laborTableData];
    updatedData[0].totalLaborAndProducts = Number.parseFloat(
      value != null ? value.toFixed(2) : updatedData[0].totalLaborAndProducts
    );

    setLaborTableData(updatedData);
  };

  const handleAdd = () => {
    if (!form) {
      console.error('form is undefined');
      return;
    }

    form
      .validateFields(['workPlaceId', 'workPlace', 'laborExtraId', 'laborExtra', 'unitMeasure', 'piece', 'price'])
      .then(
        (values: {
          workPlaceId: any;
          workPlace: any;
          piece: number;
          price: number;
          laborExtraId: any;
          laborExtra: any;
          unitMeasure: any;
        }) => {
          const currentItems = pmAdditionalActContractProject.pmContractLaborExtras || [];
          const newItem = {
            id: '',
            //totalManopera: values.piece * values.price,
            workPlaceId: values.workPlaceId,
            workPlace: values.workPlace,
            laborExtraId: values.laborExtraId,
            laborExtra: values.laborExtra,
            unitMeasure: values.unitMeasure,
            piece: values.piece,
            price: values.price,
          };
          form.setFieldsValue({
            pmContractLaborExtras: [...currentItems, newItem],
          });

          dispatch(
            updateField({
              field: 'pmContractLaborExtras',
              value: [...pmAdditionalActContractProject.pmContractLaborExtras, newItem],
            })
          );

          form.resetFields(['workPlaceId', 'laborExtraId', 'unitMeasure', 'piece', 'price']);
        }
      )
      .catch((errorInfo: any) => {
        console.error(errorInfo);
      });
  };

  const handleDeleteTempItem = (indexToRemove: any) => {
    const updatedLaborItems = pmAdditionalActContractProject.pmContractLaborExtras?.filter(
      (_item, index) => index !== indexToRemove
    );

    const recalculatedLaborItems = updatedLaborItems?.map((item, index) => ({
      ...item,
      key: index,
      totalManopera: item.piece * item.price,
    }));

    dispatch(updateField({ field: 'pmContractLaborExtras', value: recalculatedLaborItems }));
  };

  const handleUpdateTempItem = (key: any, field: string, value: any) => {
    dispatch(
      updateField({
        field: 'pmContractLaborExtras',
        value: pmAdditionalActContractProject.pmContractLaborExtras.map((item) =>
          item.key === key
            ? {
                ...item,
                [field]: value,
                totalManopera:
                  field === 'piece' || field === 'price'
                    ? (field === 'piece' ? value : item.piece) * (field === 'price' ? value : item.price) || 0
                    : item.totalManopera || 0,
              }
            : item
        ),
      })
    );
  };

  const toggleCollapse = (index: number) => {
    setOpenIndexes((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const renderColumn = (column: any, _text: string, record: any) => {
    const value = Array.isArray(column.dataIndex)
      ? column.dataIndex.reduce((acc: any, key: any) => acc[key], record)
      : record[column.dataIndex];

    if (column.editable) {
      return (
        <>
          {column.dataIndex === 'percent' && (
            <div className='input-button-container'>
              <InputNumber
                min={1}
                value={value}
                formatter={(value) => `${value}%`}
                parser={(value) => value?.replace('%', '')}
                onChange={(value) => onChangePercent(value)}
                className='totalLabor-input'
              />
              <Button
                style={{ marginLeft: '10px' }}
                onClick={() => handlePercentageChange()} // Open modal
                className='totalLabor-applyBtn'
              >
                Aplica
              </Button>
            </div>
          )}
          {column.dataIndex === 'totalLaborAndProducts' && (
            <div className='input-button-container'>
              <InputNumber
                min={1}
                value={value}
                onChange={(value) => onChangeLaborAndProducts(value!)}
                className='totalLabor-input'
              />
              <Button
                style={{ marginLeft: '10px' }}
                className='totalLabor-applyBtn'
                onClick={() => setIsModalVisible(true)} // Open modal
              >
                Aplica
              </Button>

              <Modal
                title='Optiuni de modificare pret'
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)} // Close modal on cancel
                onOk={() => handleTotalLaborAndProductsChange()} // Finalize when "Finalizeza" button is clicked
                okText='Finalizeza'
              >
                <div style={{ display: 'grid' }}>
                  <Checkbox
                    checked={selectedOption === 'updatePriceWithAddition'}
                    onChange={() => handleCheckboxChange('updatePriceWithAddition')}
                  >
                    Updateaza pret cu adaos
                  </Checkbox>
                  <Checkbox
                    checked={selectedOption === 'updateLaborPrice'}
                    onChange={() => handleCheckboxChange('updateLaborPrice')}
                  >
                    Updateaza pret manopera
                  </Checkbox>
                  <Checkbox
                    checked={selectedOption === 'updatePriceWithAdditionAndLaborPrice'}
                    onChange={() => handleCheckboxChange('updatePriceWithAdditionAndLaborPrice')}
                  >
                    Updateaza pret cu adaos si manopera
                  </Checkbox>
                </div>
              </Modal>
            </div>
          )}
        </>
      );
    }
    return value;
  };

  const columnsSecondTable: Column[] = [
    {
      title: 'Procent Manopera Adaugata',
      dataIndex: 'percent',
      key: 'percent',
      editable: true,
    },
    {
      title: 'Total Manopera Adaugata',
      dataIndex: 'totalLabor',
      key: 'totalLabor',
      render: (totalLabor: any) => Number.parseFloat(totalLabor.toFixed(2)) + ' lei',
    },
    {
      title: 'Total Valoare Produse',
      dataIndex: 'totalProductsValue',
      key: 'totalProductsValue',
      render: (totalProductsValue: any) => Number.parseFloat(totalProductsValue.toFixed(2)) + ' lei',
    },
    {
      title: 'Total Manopera + Produse',
      dataIndex: 'totalLaborAndProducts',
      key: 'totalLaborAndProducts',
      editable: true,
    },
  ];

  return (
    <div className='labor-table-container'>
      {isMobile ? (
        // Mobile View
        <Card className='labor-table-data-total'>
          <Title level={5} style={{ marginBottom: 16 }}>
            Sumar Manoperă
          </Title>
          {laborTableData.map((item: any, index: number) => (
            <div key={index}>
              {columnsSecondTable.map((column) => (
                <div key={column.key} className='labor-table-data-container'>
                  <Text strong>{column.title}:</Text>
                  <span>{renderColumn(column, '', item)}</span>
                </div>
              ))}
            </div>
          ))}
        </Card>
      ) : (
        //Desktop view
        <Card className='labor-table-data-total'>
          <Title level={5} style={{ marginBottom: 16 }}>
            Sumar Manoperă
          </Title>
          <Table
            rowClassName={'table-background-color'}
            dataSource={laborTableData}
            rowHoverable={false}
            columns={columnsSecondTable?.map((column, index) => ({
              ...column,
              key: index,
              render: column.render || ((text, record) => renderColumn(column, text, record)),
            }))}
            key={'id1'}
            pagination={false}
          />
        </Card>
      )}

      {/* Form 1 */}
      <Card className='labor-table-exatra-add'>
        <Title level={5} style={{ marginBottom: 16 }}>
          Adaugă Manoperă Suplimentară
        </Title>
        <Form form={form} layout='vertical'>
          <Row gutter={[16, 16]} align={'middle'}>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name={['workPlace', 'name']}
                label={<Text strong>Punct de lucru</Text>}
                rules={[{ required: true, message: 'Selectați punctul de lucru' }]}
              >
                <AutoComplete onSelect={handleSelectWorkPlaceLabor} placeholder='Selectați punctul de lucru'>
                  {workPlaces?.map((workPlace: { name: string }, index: number) => (
                    <AutoComplete.Option key={index} value={workPlace?.name}>
                      {workPlace?.name}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name={['pmContractLaborExtras', 'laborExtra', 'name']}
                label={<Text strong>Manoperă</Text>}
                rules={[{ required: true, message: 'Introduceți manopera' }]}
              >
                <AutoComplete onSelect={handleSelectLabor} filterOption={handleFilter} placeholder='Selectați manopera'>
                  {laborExtras?.map((item, index) => (
                    <AutoComplete.Option key={index} value={item?.name}>
                      {item?.name}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name='unitMeasure'
                label={<Text strong>Unitate de măsură</Text>}
                rules={[{ required: true, message: 'Introduceți unitatea de măsură' }]}
              >
                <AutoComplete filterOption={handleFilter} placeholder='Selectați unitatea'>
                  {unitMeasures?.map((item, index) => (
                    <AutoComplete.Option key={index} value={item}>
                      {item}
                    </AutoComplete.Option>
                  ))}
                </AutoComplete>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name='piece'
                label={<Text strong>Cantitate</Text>}
                rules={[{ required: true, message: 'Introduceți cantitatea' }]}
              >
                <InputNumber min={1} placeholder='Ex: 5' />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item
                name='price'
                label={<Text strong>Preț</Text>}
                rules={[{ required: true, message: 'Introduceți prețul' }]}
              >
                <InputNumber min={1} placeholder='Ex: 100' addonAfter='lei' style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8} lg={4}>
              <Form.Item label=' '>
                <Button type='primary' onClick={handleAdd} icon={<PlusOutlined />} style={{ width: '100%' }}>
                  Adaugă
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      {/* Form 2 */}
      {pmAdditionalActContractProject.pmContractLaborExtras?.length > 0 && (
        <Card className='labor-table-exatra-add'>
          <Title level={5} style={{ marginBottom: 16 }}>
            Manoperă Suplimentară Adăugată
          </Title>
          <Form form={form} layout='vertical'>
            <Form.List name='pmContractLaborExtras'>
              {(fields, { remove }) => (
                <>
                  {fields?.map(({ key, name, ...restField }, index) =>
                    //mobile view
                    isMobile ? (
                      <Card
                        className='pmContractLaborExtras'
                        key={key}
                        title={
                          <div className='laborExtras-title'>
                            <Button type='link' onClick={() => toggleCollapse(index)}>
                              {openIndexes.includes(index) ? <UpOutlined /> : <DownOutlined />}
                              <span>
                                <strong>Manoperă Suplimentară - {index + 1}</strong>
                              </span>
                            </Button>
                            <div>
                              <Button
                                onClick={() => {
                                  handleDeleteTempItem(index);
                                  remove(key);
                                }}
                                className='laborTable-delete-btn-sm'
                                icon={<DeleteOutlined />}
                              />
                            </div>
                          </div>
                        }
                      >
                        {openIndexes.includes(index) && (
                          <Row gutter={[8, 8]} style={{ padding: 0 }}>
                            {[
                              { name: 'workPlace.name', label: 'Punct de lucru' },
                              { name: 'laborExtra.name', label: 'Manoperă' },
                              { name: 'unitMeasure', label: 'Unitate de măsură' },
                            ].map((field, i) => (
                              <Col xs={24} sm={12} md={8} lg={4} key={i}>
                                <Form.Item
                                  {...restField}
                                  name={[name, ...field.name.split('.')]}
                                  label={<Text strong>{field.label}</Text>}
                                >
                                  <Input disabled className='disabled-input' />
                                </Form.Item>
                              </Col>
                            ))}
                            {[
                              { name: 'piece', label: 'Cantitate' },
                              { name: 'price', label: 'Preț' },
                            ].map((field, i) => (
                              <Col xs={24} sm={12} md={8} lg={4} key={i}>
                                <Form.Item
                                  {...restField}
                                  name={[name, field.name]}
                                  label={<Text strong>{field.label}</Text>}
                                >
                                  <InputNumber disabled min={1} className='disabled-input' />
                                </Form.Item>
                              </Col>
                            ))}
                            <Col xs={24}>
                              <div className='total-manopera'>
                                <Text strong>Total:</Text>
                                <Text type='success' strong>
                                  {pmAdditionalActContractProject.pmContractLaborExtras[index]?.piece *
                                    pmAdditionalActContractProject.pmContractLaborExtras[index]?.price}{' '}
                                  lei
                                </Text>
                              </div>
                            </Col>
                          </Row>
                        )}
                      </Card>
                    ) : (
                      <div key={key} className='labor-extras-item'>
                        <Row gutter={[16, 16]} align='middle' style={{ alignItems: 'flex-start' }}>
                          <Col xs={24} sm={12} md={8} lg={4}>
                            <Form.Item {...restField} name={[name, 'workPlace', 'name']}>
                              <Input disabled className='disabled-input' />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={4}>
                            <Form.Item {...restField} name={[name, 'laborExtra', 'name']}>
                              <Input disabled className='disabled-input' />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={4}>
                            <Form.Item {...restField} name={[name, 'unitMeasure']}>
                              <Input disabled className='disabled-input' />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={4}>
                            <Form.Item {...restField} name={[name, 'piece']}>
                              <InputNumber
                                disabled
                                min={1}
                                className=' disabled-input'
                                onChange={(value) => handleUpdateTempItem(index, 'piece', value)}
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={4}>
                            <Form.Item {...restField} name={[name, 'price']}>
                              <InputNumber
                                disabled
                                min={1}
                                className=' disabled-input'
                                style={{ width: '100%' }}
                                onChange={(value) => handleUpdateTempItem(index, 'price', value)}
                                addonAfter='lei'
                              />
                            </Form.Item>
                          </Col>
                          <Col xs={24} sm={12} md={8} lg={4}>
                            <Button
                              danger
                              className='laborTable-delete-btn-lg'
                              onClick={() => {
                                handleDeleteTempItem(index);
                                remove(key);
                              }}
                              icon={<DeleteOutlined />}
                            >
                              Șterge
                            </Button>
                          </Col>
                        </Row>
                        <Divider style={{ margin: '8px 0' }} />
                      </div>
                    )
                  )}
                </>
              )}
            </Form.List>
          </Form>
        </Card>
      )}

      {/* Modal for price adjustment options */}
      <Modal
        title={
          <div>
            <CalculatorOutlined /> Opțiuni de modificare preț
          </div>
        }
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={() => handleTotalLaborAndProductsChange()}
        okText='Finalizează'
        cancelText='Anulează'
        okButtonProps={{ disabled: !selectedOption }}
        className='price-adjustment-modal'
      >
        <div className='price-options'>
          <Checkbox
            checked={selectedOption === 'updatePriceWithAddition'}
            onChange={() => handleCheckboxChange('updatePriceWithAddition')}
          >
            <Text strong>Actualizează preț cu adaos</Text>
            <div>
              <Text type='secondary'>Modifică doar prețurile produselor, păstrând manopera neschimbată</Text>
            </div>
          </Checkbox>
          <Checkbox
            checked={selectedOption === 'updateLaborPrice'}
            onChange={() => handleCheckboxChange('updateLaborPrice')}
          >
            <Text strong>Actualizează preț manoperă</Text>
            <div>
              <Text type='secondary'>Modifică doar prețurile manoperei, păstrând prețurile produselor neschimbate</Text>
            </div>
          </Checkbox>
          <Checkbox
            checked={selectedOption === 'updatePriceWithAdditionAndLaborPrice'}
            onChange={() => handleCheckboxChange('updatePriceWithAdditionAndLaborPrice')}
          >
            <Text strong>Actualizează preț cu adaos și manoperă</Text>
            <div>
              <Text type='secondary'>Modifică proporțional atât prețurile produselor cât și manopera</Text>
            </div>
          </Checkbox>
        </div>
      </Modal>
    </div>
  );
};

export default LaborTable;
